import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../components/ui/tooltip';
import { 
  Plus, 
  CalendarDays,
  Clock,
  CheckCircle,
  AlertCircle,
  History,
  Calendar
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Separate component to avoid babel plugin recursion issues
function AbsenceList({ absences, locale, t, noDataText, showStatus }) {
  if (!absences || absences.length === 0) {
    return <p className="text-xs text-muted-foreground">{noDataText}</p>;
  }
  
  return (
    <ul className="text-xs space-y-1">
      {absences.map((absence, idx) => (
        <li key={idx} className="flex items-center gap-1">
          {showStatus && (
            <span className={`w-2 h-2 rounded-full ${absence.status === 'approved' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          )}
          <span>
            {format(parseISO(absence.start_date), 'dd/MM/yyyy', { locale })}
            {absence.start_date !== absence.end_date && ` → ${format(parseISO(absence.end_date), 'dd/MM/yyyy', { locale })}`}
            {absence.day_part && absence.day_part !== 'full_day' && ` (${t(absence.day_part)})`}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function LeaveManagement() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  const [allocations, setAllocations] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [newAllocation, setNewAllocation] = useState({
    user_id: '',
    year: new Date().getFullYear(),
    allocated_hours: 160
  });

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  const fetchAllocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leave-allocations`, {
        params: { year: selectedYear }
      });
      setAllocations(res.data);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [selectedYear, t]);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team`);
      setTeam(res.data);
    } catch (err) {
      console.error('Error fetching team:', err);
    }
  }, []);

  useEffect(() => {
    fetchAllocations();
    fetchTeam();
  }, [fetchAllocations, fetchTeam]);

  const handleSaveAllocation = async () => {
    try {
      await axios.post(`${API}/leave-allocations`, newAllocation);
      toast.success(t('allocationSaved'));
      setDialogOpen(false);
      fetchAllocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const getProgressColor = (remaining, total) => {
    const percentage = (remaining / total) * 100;
    if (percentage > 50) return 'bg-emerald-500';
    if (percentage > 25) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="space-y-6" data-testid="leave-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl">{t('leaveBalance').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">
            {t('annualLeave')} {selectedYear}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]" data-testid="year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button onClick={() => setDialogOpen(true)} data-testid="allocate-leave-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('allocateLeave')}
          </Button>
        </div>
      </div>

      {/* Leave Cards */}
      {allocations.length === 0 ? (
        <div className="border border-border rounded-lg p-8 text-center">
          <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t('noDataAvailable')}</p>
          <p className="text-sm text-muted-foreground mt-2">
            {language === 'de' ? 'Klicken Sie auf "Urlaub zuteilen" um Stunden zuzuweisen' :
             language === 'en' ? 'Click "Allocate leave" to assign hours' :
             'Cliquez sur "Allouer des congés" pour attribuer des heures'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allocations.map((allocation) => {
            const usedPercentage = (allocation.used_hours / allocation.allocated_hours) * 100;
            const plannedPercentage = (allocation.planned_hours / allocation.allocated_hours) * 100;
            const remainingPercentage = (allocation.remaining_hours / allocation.allocated_hours) * 100;
            
            return (
              <div 
                key={allocation.id} 
                className="border border-border rounded-lg p-4 bg-card hover:border-primary transition-colors"
                data-testid={`leave-card-${allocation.user_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-medium text-lg">{allocation.user_name}</h3>
                    <p className="text-sm text-muted-foreground">{selectedYear}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{allocation.remaining_hours}{t('h')}</p>
                    <p className="text-xs text-muted-foreground">{t('hoursRemaining')}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2 mb-4">
                  <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                    <div 
                      className="bg-emerald-500 h-full transition-all"
                      style={{ width: `${usedPercentage}%` }}
                      title={`${t('hoursUsed')}: ${allocation.used_hours}h`}
                    />
                    <div 
                      className="bg-amber-500 h-full transition-all"
                      style={{ width: `${plannedPercentage}%` }}
                      title={`${t('hoursPlanned')}: ${allocation.planned_hours}h`}
                    />
                    <div 
                      className="bg-primary/20 h-full transition-all"
                      style={{ width: `${remainingPercentage}%` }}
                      title={`${t('hoursRemaining')}: ${allocation.remaining_hours}h`}
                    />
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-2 text-sm">
                  {/* Previous year balance */}
                  {allocation.prev_year_remaining !== undefined && allocation.prev_year_remaining !== 0 && (
                    <div className="flex justify-between bg-blue-50 dark:bg-blue-900/20 rounded px-2 py-1 -mx-2">
                      <span className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                        <History className="w-3 h-3" />
                        {t('prevYearBalance')} ({selectedYear - 1})
                      </span>
                      <span className="font-medium text-blue-700 dark:text-blue-400">
                        {allocation.prev_year_remaining_days}{t('days')} ({allocation.prev_year_remaining}{t('h')})
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-muted" />
                      {t('hoursAllocated')}
                    </span>
                    <span className="font-medium">{allocation.allocated_days || 0}{t('days')} ({allocation.allocated_hours}{t('h')})</span>
                  </div>
                  
                  {/* Used hours with tooltip showing last 5 absences */}
                  <div className="flex justify-between">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-2 cursor-help">
                          <div className="w-3 h-3 rounded-full bg-emerald-500" />
                          {t('hoursUsed')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[250px]">
                        <p className="font-medium mb-1">{t('lastAbsences')}</p>
                        <AbsenceList absences={allocation.last_absences} locale={locale} t={t} noDataText={t('noAbsences')} />
                        {allocation.used_from_prev_year > 0 && (
                          <p className="text-xs text-blue-500 mt-2 pt-2 border-t">
                            {t('deductedFromPrevYear')}: {(allocation.used_from_prev_year / 8).toFixed(1)}{t('days')}
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                    <div className="text-right">
                      <span className="font-medium text-emerald-600">{allocation.used_days || 0}{t('days')} ({allocation.used_hours}{t('h')})</span>
                      {allocation.used_from_prev_year > 0 && (
                        <div className="text-xs text-blue-500">
                          {t('fromN1')}: {(allocation.used_from_prev_year / 8).toFixed(1)}{t('days')}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Planned hours with tooltip showing next 5 absences */}
                  <div className="flex justify-between">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-2 cursor-help">
                          <div className="w-3 h-3 rounded-full bg-amber-500" />
                          {t('hoursPlanned')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[250px]">
                        <p className="font-medium mb-1">{t('nextAbsences')}</p>
                        <AbsenceList absences={allocation.next_absences} locale={locale} t={t} noDataText={t('noPlannedAbsences')} showStatus />
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-medium text-amber-600">{allocation.planned_days || 0}{t('days')} ({allocation.planned_hours}{t('h')})</span>
                  </div>
                  
                  <div className="flex justify-between border-t border-border pt-2 mt-2">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary" />
                      {t('hoursRemaining')}
                    </span>
                    <span className={`font-bold ${allocation.remaining_hours < 0 ? 'text-red-500' : 'text-primary'}`}>
                      {allocation.remaining_days}{t('days')} ({allocation.remaining_hours}{t('h')})
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Allocate Leave Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('allocateLeave').toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>{t('collaborator')}</Label>
              <Select
                value={newAllocation.user_id}
                onValueChange={(v) => setNewAllocation({ ...newAllocation, user_id: v })}
              >
                <SelectTrigger data-testid="allocation-user-select">
                  <SelectValue placeholder={t('collaborator')} />
                </SelectTrigger>
                <SelectContent>
                  {team.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('year')}</Label>
              <Select
                value={newAllocation.year.toString()}
                onValueChange={(v) => setNewAllocation({ ...newAllocation, year: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('allocatedHours')}</Label>
              <Input
                type="number"
                value={newAllocation.allocated_hours}
                onChange={(e) => setNewAllocation({ ...newAllocation, allocated_hours: parseFloat(e.target.value) })}
                data-testid="allocation-hours-input"
              />
              <p className="text-xs text-muted-foreground">
                {language === 'de' ? '160h = 20 Tage × 8h' :
                 language === 'en' ? '160h = 20 days × 8h' :
                 '160h = 20 jours × 8h'}
              </p>
            </div>

            <Button onClick={handleSaveAllocation} className="w-full" data-testid="save-allocation-btn">
              {t('saveAllocation')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
